import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  ClinicalReasonInput,
  DiscardEyeExaminationInput,
  EligibleExaminer,
  EyeExaminationDetail,
  EyeExaminationSummary,
  PaginatedResult,
  SaveEyeExaminationInput,
  UpdateEyeExaminationInput,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';

/**
 * Eye examinations over the API.
 *
 * Keys and paths carry identifiers only; clinical values travel in request bodies and live in memory, never in a URL, a query string or browser storage.
 */
export const medicalKeys = {
  all: ['medical'] as const,
  customerExaminations: (customerId: string, includeVoided: boolean) =>
    [...medicalKeys.all, 'customer', customerId, 'examinations', includeVoided] as const,
  examination: (id: string) => [...medicalKeys.all, 'examination', id] as const,
  branchExaminers: (branchId: string) =>
    [...medicalKeys.all, 'examiners', 'branch', branchId] as const,
  examinationExaminers: (id: string) =>
    [...medicalKeys.all, 'examiners', 'examination', id] as const,
};

export function useCustomerExaminationsQuery(
  customerId: string | undefined,
  includeVoided = false,
  enabled = true,
) {
  return useQuery({
    queryKey: medicalKeys.customerExaminations(customerId ?? '', includeVoided),
    queryFn: () =>
      api.get<PaginatedResult<EyeExaminationSummary>>(
        `/customers/${customerId ?? ''}/eye-examinations`,
        { params: includeVoided ? { includeVoided: true, pageSize: 20 } : { pageSize: 20 } },
      ),
    enabled: enabled && Boolean(customerId),
  });
}

/** One examination in full. Every fetch is a read the API records with identifiers only. */
export function useEyeExaminationQuery(id: string | undefined) {
  return useQuery({
    queryKey: medicalKeys.examination(id ?? ''),
    queryFn: () => api.get<EyeExaminationDetail>(`/eye-examinations/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/** Who may be named examiner on an examination not yet saved: the current branch's eligible examiners. */
export function useBranchExaminersQuery(branchId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: medicalKeys.branchExaminers(branchId ?? ''),
    queryFn: () => api.get<EligibleExaminer[]>('/medical/examiners'),
    enabled: enabled && Boolean(branchId),
  });
}

/** Who may be named examiner on a saved examination: eligible in that examination's own branch. */
export function useExaminationExaminersQuery(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: medicalKeys.examinationExaminers(id ?? ''),
    queryFn: () => api.get<EligibleExaminer[]>(`/eye-examinations/${id ?? ''}/examiners`),
    enabled: enabled && Boolean(id),
  });
}

export function useCreateEyeExaminationMutation(customerId: string) {
  const queryClient = useQueryClient();

  return useIdempotentMutation<EyeExaminationDetail, SaveEyeExaminationInput>(
    (body, config) =>
      api.post<EyeExaminationDetail>(`/customers/${customerId}/eye-examinations`, body, config),
    {
      onSuccess: (detail) => {
        queryClient.setQueryData(medicalKeys.examination(detail.id), detail);
        void queryClient.invalidateQueries({
          queryKey: [...medicalKeys.all, 'customer', customerId],
        });
      },
    },
  );
}

/** Saves the whole draft. The body carries the version the screen was showing; a stale one is refused, never retried over. */
export function useSaveEyeExaminationMutation(id: string) {
  const queryClient = useQueryClient();

  return useIdempotentMutation<EyeExaminationDetail, UpdateEyeExaminationInput>(
    (body, config) => api.put<EyeExaminationDetail>(`/eye-examinations/${id}`, body, config),
    {
      onSuccess: (detail) => {
        queryClient.setQueryData(medicalKeys.examination(detail.id), detail);
        void queryClient.invalidateQueries({
          queryKey: [...medicalKeys.all, 'customer', detail.customerId],
        });
      },
    },
  );
}

/** The record a lifecycle change answered with goes straight into the cache; the lists, and a record it replaced, are read again. */
function adoptLifecycleResult(queryClient: QueryClient, detail: EyeExaminationDetail) {
  queryClient.setQueryData(medicalKeys.examination(detail.id), detail);
  void queryClient.invalidateQueries({
    queryKey: [...medicalKeys.all, 'customer', detail.customerId],
  });
  if (detail.replacesExaminationId) {
    void queryClient.invalidateQueries({
      queryKey: medicalKeys.examination(detail.replacesExaminationId),
    });
  }
}

/** Signs the draft off. Completing an amendment also replaces the examination it corrects, so that record is read again too. */
export function useCompleteEyeExaminationMutation(id: string) {
  const queryClient = useQueryClient();

  return useIdempotentMutation<EyeExaminationDetail, { version: number }>(
    (body, config) =>
      api.post<EyeExaminationDetail>(`/eye-examinations/${id}/complete`, body, config),
    { onSuccess: (detail) => adoptLifecycleResult(queryClient, detail) },
  );
}

/** Starts correcting a completed examination: a new draft copied from it. Idempotent on the server, so a retried click answers with the same draft. */
export function useAmendEyeExaminationMutation(id: string) {
  const queryClient = useQueryClient();

  return useIdempotentMutation<EyeExaminationDetail, ClinicalReasonInput>(
    (body, config) =>
      api.post<EyeExaminationDetail>(`/eye-examinations/${id}/amendments`, body, config),
    { onSuccess: (detail) => adoptLifecycleResult(queryClient, detail) },
  );
}

/** Voids a completed examination. The server refuses a second void by the record's status, so nothing is ever retried here. */
export function useVoidEyeExaminationMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ClinicalReasonInput) =>
      api.post<EyeExaminationDetail>(`/eye-examinations/${id}/void`, body),
    onSuccess: (detail) => adoptLifecycleResult(queryClient, detail),
  });
}

/** Discards a draft, which is kept. The version guards it: a stale or repeated discard is refused, never retried. */
export function useDiscardEyeExaminationMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: DiscardEyeExaminationInput) =>
      api.post<EyeExaminationDetail>(`/eye-examinations/${id}/discard`, body),
    onSuccess: (detail) => adoptLifecycleResult(queryClient, detail),
  });
}
